import { useEffect, useState } from "react";

import styles from "./update-notice.module.css";

/** Written by `vite.config.ts` at build time; the same id is in `/version.json`. */
declare const __BUILD_ID__: string;

/** How often an open tab asks whether a newer version was deployed. */
const CHECK_MS = 5 * 60_000;

/**
 * "A new version of DentaCore is ready" — 2026-10-05.
 *
 * **The app loads its code once per tab.** Moving between screens never fetches
 * new code, so a tab opened before a deploy keeps running the old version —
 * which is how a deployed Suppliers tab stayed invisible to the person who had
 * asked for it. This compares the build this tab is running with the one the
 * server has now: on returning to the tab, and every five minutes.
 *
 * **It offers a reload and never forces one**: a reload in the middle of a
 * half-typed note or booking would lose it, and the person knows whether they
 * are in the middle of something.
 *
 * Production only — the dev server has no `version.json` and replaces code as
 * it changes anyway.
 */
export function UpdateNotice(): React.JSX.Element | null {
  const [stale, setStale] = useState(false);

  useEffect(() => {
    if (!import.meta.env.PROD) return;

    let stopped = false;
    const check = async (): Promise<void> => {
      try {
        // `no-store`, so the answer is the server's now and not a copy the
        // browser kept — the very thing this exists to get around.
        const response = await fetch(`/version.json?t=${String(Date.now())}`, {
          cache: "no-store",
        });
        if (!response.ok) return;
        const body = (await response.json()) as { build?: unknown };
        if (!stopped && typeof body.build === "string" && body.build !== __BUILD_ID__) {
          setStale(true);
        }
      } catch {
        /* Offline, or mid-deploy: ask again next time. */
      }
    };

    const onVisible = (): void => {
      if (document.visibilityState === "visible") void check();
    };
    const timer = window.setInterval(() => void check(), CHECK_MS);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      stopped = true;
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, []);

  if (!stale) return null;

  return (
    <div className={styles.notice} role="status" data-testid="update-notice">
      <span>A new version of DentaCore is ready.</span>
      <button type="button" className={styles.reload} onClick={() => window.location.reload()}>
        Reload
      </button>
      <button
        type="button"
        className={styles.later}
        onClick={() => setStale(false)}
        aria-label="Not now"
      >
        Later
      </button>
    </div>
  );
}
